<script setup>
import { userAuthState } from "@/@core/auth"
import { SurveyType } from "@/enums/survey/survey.type"
import { useRoute, useRouter } from 'vue-router'
import { VCardItem, VCardText } from "vuetify/lib/components/index.mjs"
import { VStepper, VStepperHeader, VStepperItem } from 'vuetify/lib/labs/components.mjs'

definePage({
  meta: {
    name: "Survey Collection View",

    // subject: "service_desk.menu",
    // action: "dashboard",
  },
})

const route = useRoute()
const router = useRouter()

const {
  data: surveyResponse,
} = await useApi(createUrl(`/survey/survey-responses/${route.params.id}`), {
  beforeFetch: () => {},
  onFetchError: error => {
    router.push({
      name: 'invalid-url',
      params: { code: 404 },
    })
  },
})

const currentStep = ref(0)
</script>

<template>
  <VRow>
    <VCol cols="8">
      <VCard v-if="surveyResponse.collected_at">
        <VCardItem>
          <VCardTitle>
            Survey Responses
          </VCardTitle>
        </VCardItem>

        <VDivider />

        <VCardItem style="padding: 4px 24px !important;">
          <VStepper
            v-model="currentStep"
            show-actions
            class="pa-0"
            style="box-shadow: none !important;"
          >
            <template #default="{ prev, next }">
              <VStepperHeader style="box-shadow: none !important;">
                <template
                  v-for="(formStep, formStepIndex) in surveyResponse.survey.form_steps"
                  :key="`${formStepIndex}-step`"
                >
                  <VStepperItem
                    :value="formStepIndex"
                    :icon="formStep.icon"
                    :title="formStep.title"
                  />

                  <VDivider
                    v-if="formStepIndex + 1 !== surveyResponse.survey.form_steps?.length"
                    :key="formStepIndex"
                  />
                </template>
              </VStepperHeader>

              <!-- <VDivider /> -->
            </template>
          </VStepper>
        </VCardItem>

        <VCardText>
          <!--
            <VStepperWindow v-model="currentStep">
            <VStepperWindowItem
            v-for="(formStep, formStepIndex) in surveyResponse.survey.form_steps"
            :key="`${formStepIndex}-content`"
            >
            {{ formStep.id }}
            <VCard
            color="grey-lighten-1"
            flat
            >
            <VCardText class="d-flex flex-column gap-4">
            <div
            v-for="(questionGroup, questionGroupIndex) in formStep.question_groups"
            :key="questionGroupIndex"
            class="px-3 py-3 border-sm rounded-lg"
            >
            <h6 class="text-base font-weight-medium text-primary">
            {{ questionGroup.title }}
            </h6>
            <VDivider class="mt-2 mb-3" />
                              
            <div
            v-for="(question, questionIndex) in questionGroup.questions"
            :key="questionIndex"
            >
            <p class="mb-2 text-body-1 font-weight-medium mt-1">
            <span class="me-3 custom-background pa-1 rounded text-primary ">Q.{{ questionIndex + 1 }}  </span> {{ question.label }}
            </p>
            <p class="mb-4 text-body-1 px-1 py-3 rounded-lg custom-background text-high-emphasis border-sm">
            <span class="ms-1 me-2"> Ans:</span> {{ question.answer.response }}
            </p>
            </div>
            </div>
            </VCardText>
            </VCard>
            </VStepperWindowItem>
            </VStepperWindow> 
          -->

          <VWindow
            v-model="currentStep"
            style="min-block-size: 400px;"
            class="disable-tab-transition"
          >
            <template
              v-for="(formStep, formStepIndex) in surveyResponse.survey.form_steps"
              :key="formStepIndex"
            >
              <VWindowItem class="d-flex flex-column gap-4 bg-var-theme-white">
                <div
                  v-for="(questionGroup, questionGroupIndex) in formStep.question_groups"
                  :key="questionGroupIndex"
                  class="px-3 py-3 border-sm rounded-lg"
                >
                  <h6 class="text-base font-weight-medium text-primary">
                    {{ questionGroup.title }}
                  </h6>
                  <VDivider class="mt-2 mb-3" />
                              
                  <div
                    v-for="(question, questionIndex) in questionGroup.questions"
                    :key="questionIndex"
                  >
                    <p class="mb-2 text-body-1 font-weight-medium mt-1">
                      <span class="me-3 custom-background pa-1 rounded text-primary ">Q.{{ questionIndex + 1 }}  </span> {{ question.label }}
                    </p>
                    <p class="mb-4 text-body-1 px-1 py-3 rounded-lg custom-background text-high-emphasis border-sm">
                      <span class="ms-1 me-2"> Ans:</span> {{ question?.answer?.response ?? '--' }}
                    </p>
                  </div>
                </div>
              </VWindowItem>
            </template>
          </VWindow>
        </VCardText>

        <VCardText>
          <div class="d-flex align-center">
            <VBtn
              color="secondary"
              variant="tonal"
              prepend-icon="tabler-arrow-left"
              :disabled="currentStep === 0"
              @click="currentStep--"
            >
              Previous
            </VBtn>

            <VSpacer />

            <VBtn 
              color="secondary"
              variant="tonal"
              append-icon="tabler-arrow-right"
              :disabled="currentStep === surveyResponse.survey?.form_steps?.length - 1"
              @click="currentStep ++"
            >
              Continue
            </VBtn>
          </div>
        </VCardText>
      </VCard>
      <VAlert
        v-else
        color="warning"
        variant="tonal"
        icon="tabler-alert-triangle"
      >
        Survey is not completed yet.
      </VAlert>
    </VCol>
    <VCol
      cols="4"
      class="d-flex flex-column gap-6"
    >
      <VCard>
        <VCardItem>
          <VCardTitle>
            Participant
          </VCardTitle>
        </VCardItem>

        <VDivider />

        <VCardText>
          <!-- Customer -->
          <VRow
            v-if="surveyResponse.survey.type === SurveyType.CUSTOMER"
            no-gutters
            class="d-flex flex-column gap-1 text-title-1"
          >
            <VCol class="d-flex">
              <span style="inline-size: 95px"> UHID</span> : {{ surveyResponse.episode.customer.uhid || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Name</span> : {{ surveyResponse.episode.customer.name || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Phone No</span> : {{ surveyResponse.episode.customer.phone || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Email</span> : {{ surveyResponse.episode.customer.email || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Unit</span> : {{ surveyResponse.episode.unit.name || 'N/A' }}
            </VCol>
          </VRow>

          <!-- Participant -->
          <VRow
            v-if="surveyResponse.survey.type === SurveyType.EMPLOYEE || surveyResponse.survey.type === SurveyType.EVENT"
            no-gutters
            class="d-flex flex-column gap-1 text-title-1"
          >
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Name</span> : {{ surveyResponse.participant.name }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Phone No</span> : {{ surveyResponse.participant.phone || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Email</span> : {{ surveyResponse.participant.email || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Unit</span> : {{ surveyResponse.participant.unit.name || 'N/A' }}
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <VCard>
        <VCardItem>
          <VCardTitle>
            Survey
          </VCardTitle>
        </VCardItem>

        <VDivider />

        <VCardText>
          <VRow
            no-gutters
            class="d-flex flex-column gap-1 text-title-1"
          >
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Title</span> : {{ surveyResponse.survey?.title }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Description</span> : {{ surveyResponse.survey?.description }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Schedule At</span> : {{ formatDate(surveyResponse.schedule_at) || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Triggered At</span> : {{ formatDate(surveyResponse.schedule_at) || 'N/A' }}
            </VCol>
            <VCol class="d-flex">
              <span style="inline-size: 95px"> Collected At</span> : N/A
            </VCol>
          </VRow>
        </VCardText>

        <VCardText>
          <VBtn
            variant="flat"
            block
            append-icon="tabler-external-link"
            @click="router.push({ name: 'app-survey-customer-setup', params: { type: surveyResponse.survey.type, survey: surveyResponse.survey.id } })"
          >
            View Survey
          </VBtn>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>
